import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

type Language = 'en' | 'vi';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: Record<string, string>;
}

const translations = {
  en: {
    // Sidebar & Nav
    dashboards: "Dashboards",
    overview: "Overview",
    projectMenu: "Project Menu",
    analytics: "Analytics",
    shareReport: "Share Report",
    linkBuilder: "Link Builder",
    installCode: "Install Code",
    searchPlaceholder: "Search projects...",
    gdprSettings: "GDPR Settings",
    accessibility: "Accessibility",
    
    // Dashboard Page
    dashboardTitle: "Dashboards",
    dashboardSubtitle: "Overview of all your tracked properties",
    addNewProject: "Add New Project",
    noProjects: "No active projects",
    startTracking: "Start tracking your first website to see real-time insights and heatmaps.",
    createFirstProject: "Create first project",
    liveTracking: "Live Tracking",
    viewReport: "View Report",
    newProjectModal: "New Project",
    startTrackingModal: "Start tracking a new domain",
    websiteName: "Website Name",
    domainUrl: "Domain URL",
    cancel: "Cancel",
    createProject: "Create Project",
    
    // Report Page - Headers
    welcomeBack: "Welcome Back!",
    welcomeMessage: "Here's your website analytics for",
    youHave: "You Have Total Of",
    newVisitsToday: "new visits",
    quickView: "Quick View",
    
    // Stats Cards
    statUsers: "Users",
    statSessions: "Sessions",
    statDuration: "Avg. Duration",
    statBounceRate: "Bounce Rate",
    thisMonth: "this month",
    
    // Charts Titles
    clicksTracking: "Clicks Tracking",
    trafficOverview: "Traffic overview",
    pageViews: "Page Views",
    trending: "Trending",
    whereCustomersFrom: "Where Customers From",
    topSources: "Top sources",
    timeTracking: "Time Tracking",
    peakActivity: "Peak Activity",
    userByDevice: "User By Device",
    deviceDist: "Device distribution",
    topPagesMinute: "Top Browsing Pages Per Minute",
    visitsByCity: "Visits By City",
    geoBreakdown: "Geographic breakdown",
    
    // Table & Lists
    page: "Page",
    views: "Views",
    bounceRate: "Bounce Rate",
    noPageViews: "No page views recorded yet.",
    noLocationData: "No location data for this period.",
    
    // Time Filters
    today: "Today",
    week: "This Week",
    month: "This Month",
    year: "This Year",
    custom: "Custom Range",
    from: "From",
    to: "To",

    // Misc
    loading: "Loading...",
    copied: "Copied",
    copyCode: "Copy Code",
    verify: "Verify Installation",
    troubleshoot: "Troubleshoot",
    setupGuide: "Setup Guide for",
    excludeBots: "Exclude Bot Traffic",
    botTrafficDesc: "Hide traffic from known data centers (Omaha, NE & The Dalles, OR)"
  },
  vi: {
    // Sidebar & Nav
    dashboards: "Bảng điều khiển",
    overview: "Tổng quan",
    projectMenu: "Menu Dự Án",
    analytics: "Phân tích",
    shareReport: "Chia sẻ báo cáo",
    linkBuilder: "Tạo liên kết",
    installCode: "Cài đặt mã",
    searchPlaceholder: "Tìm dự án...",
    gdprSettings: "Cài đặt GDPR",
    accessibility: "Phụ trợ (Accessibility)",
    
    // Dashboard Page
    dashboardTitle: "Bảng điều khiển",
    dashboardSubtitle: "Tổng quan về các tài sản được theo dõi",
    addNewProject: "Thêm dự án mới",
    noProjects: "Không có dự án hoạt động",
    startTracking: "Bắt đầu theo dõi trang web đầu tiên của bạn để xem thông tin chi tiết.",
    createFirstProject: "Tạo dự án đầu tiên",
    liveTracking: "Đang theo dõi",
    viewReport: "Xem báo cáo",
    newProjectModal: "Dự Án Mới",
    startTrackingModal: "Bắt đầu theo dõi tên miền mới",
    websiteName: "Tên trang web",
    domainUrl: "URL tên miền",
    cancel: "Hủy",
    createProject: "Tạo Dự Án",
    
    // Report Page - Headers
    welcomeBack: "Chào mừng trở lại!",
    welcomeMessage: "Đây là phân tích trang web cho",
    youHave: "Bạn có tổng cộng",
    newVisitsToday: "lượt truy cập mới",
    quickView: "Xem nhanh",
    
    // Stats Cards
    statUsers: "Người dùng",
    statSessions: "Phiên truy cập",
    statDuration: "T.g trung bình",
    statBounceRate: "Tỷ lệ thoát",
    thisMonth: "tháng này",
    
    // Charts Titles
    clicksTracking: "Theo dõi lượt nhấp",
    trafficOverview: "Tổng quan lưu lượng",
    pageViews: "Lượt xem trang",
    trending: "Xu hướng",
    whereCustomersFrom: "Nguồn khách hàng",
    topSources: "Nguồn hàng đầu",
    timeTracking: "Theo dõi thời gian",
    peakActivity: "Hoạt động cao điểm",
    userByDevice: "Thiết bị người dùng",
    deviceDist: "Phân bố thiết bị",
    topPagesMinute: "Các trang hàng đầu mỗi phút",
    visitsByCity: "Truy cập theo thành phố",
    geoBreakdown: "Phân bố địa lý",
    
    // Table & Lists
    page: "Trang",
    views: "Lượt xem",
    bounceRate: "Tỷ lệ thoát",
    noPageViews: "Chưa có lượt xem trang nào được ghi nhận.",
    noLocationData: "Không có dữ liệu vị trí cho giai đoạn này.",
    
    // Time Filters
    today: "Hôm nay",
    week: "Tuần này",
    month: "Tháng này",
    year: "Năm nay",
    custom: "Tùy chỉnh",
    from: "Từ",
    to: "Đến",

    // Misc
    loading: "Đang tải...",
    copied: "Đã sao chép",
    copyCode: "Sao chép mã",
    verify: "Xác minh cài đặt",
    troubleshoot: "Khắc phục sự cố",
    setupGuide: "Hướng dẫn cài đặt cho",
    excludeBots: "Loại bỏ truy cập ảo",
    botTrafficDesc: "Ẩn lưu lượng từ các trung tâm dữ liệu (Omaha, NE & The Dalles, OR)"
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const stored = localStorage.getItem('app_language');
      return (stored === 'en' || stored === 'vi') ? stored : 'en';
    } catch (e) {
      return 'en';
    }
  });

  useEffect(() => {
    localStorage.setItem('app_language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'vi' : 'en');
  };

  const value = {
    language,
    toggleLanguage,
    t: translations[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};